import React from 'react';
import {
    Box, Container, Typography, Button, Card, CardContent,
    Stack, Chip,
} from '@mui/material';
import {
    Download, FolderSpecial, AutoAwesome, Settings, Speed,
    Dashboard, CheckCircle,
} from '@mui/icons-material';
import { routes } from '../routes';
import { Link as RouterLink } from 'react-router';
import Logo02 from '../assets/02-logo-menu-cuadrado.svg';
import { useServiceStatus } from '../hooks/useServiceStatus';
import { useTranslation } from 'react-i18next';

const DOWNLOAD_URL = 'https://releases.fileswatcher.com/FilesWatcher-Setup-v1.0.0.exe';

function BrandLogo({ size = 40 }: { size?: number }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box component="img" src={Logo02} alt="FilesWatcher" sx={{ height: size, width: size, borderRadius: 1.5 }} />
            <Typography sx={{ fontWeight: 700, fontSize: size * 0.35, lineHeight: 1 }}>
                <Box component="span" sx={{ color: 'white' }}>Files</Box>
                <Box component="span" sx={{ color: '#38BDF8' }}>Watcher</Box>
            </Typography>
        </Box>
    );
}

const LandingPage: React.FC = () => {
    const { data: serviceReachable } = useServiceStatus();
    const { t, i18n } = useTranslation();

    const handleLanguageChange = (lng: string) => {
        i18n.changeLanguage(lng);
        localStorage.setItem('i18nextLng', lng);
    };

    const FEATURES = [
        {
            icon: <AutoAwesome sx={{ fontSize: 28 }} />,
            title: t('landing.feature1Title'),
            desc: t('landing.feature1Desc'),
            color: '#2563EB',
            bg: 'rgba(37,99,235,0.1)',
        },
        {
            icon: <FolderSpecial sx={{ fontSize: 28 }} />,
            title: t('landing.feature2Title'),
            desc: t('landing.feature2Desc'),
            color: '#0EA5E9',
            bg: 'rgba(14,165,233,0.1)',
        },
        {
            icon: <Speed sx={{ fontSize: 28 }} />,
            title: t('landing.feature3Title'),
            desc: t('landing.feature3Desc'),
            color: '#10B981',
            bg: 'rgba(16,185,129,0.1)',
        },
        {
            icon: <Settings sx={{ fontSize: 28 }} />,
            title: t('landing.feature4Title'),
            desc: t('landing.feature4Desc'),
            color: '#F59E0B',
            bg: 'rgba(245,158,11,0.1)',
        },
    ];

    const STEPS = [
        { n: 1, title: t('landing.step1Title'), desc: t('landing.step1Desc') },
        { n: 2, title: t('landing.step2Title'), desc: t('landing.step2Desc') },
        { n: 3, title: t('landing.step3Title'), desc: t('landing.step3Desc') },
    ];

    const FREE_FEATURES = [
        t('landing.freeFeature1'),
        t('landing.freeFeature2'),
        t('landing.freeFeature3'),
    ];

    const PREMIUM_FEATURES = [
        t('landing.premiumFeature1'),
        t('landing.premiumFeature2'),
        t('landing.premiumFeature3'),
        t('landing.premiumFeature4'),
    ];

    const MOCK_KPI = [
        { label: t('landing.mockKpiTotal'), value: '8', borderColor: '#2563EB', valueColor: '#0F172A' },
        { label: t('landing.mockKpiActive'), value: '7', borderColor: '#10B981', valueColor: '#10B981' },
        { label: t('landing.mockKpiInactive'), value: '1', borderColor: '#F59E0B', valueColor: '#0F172A' },
        { label: t('landing.mockKpiToday'), value: '127', borderColor: '#38BDF8', valueColor: '#0EA5E9' },
    ];

    const MOCK_DASHBOARD_RULES = [
        { name: 'PDFs → Documentos', ext: '.pdf' },
        { name: 'Screenshots → Imágenes', ext: '.png' },
        { name: 'Facturas → Contabilidad', ext: '.pdf' },
        { name: 'Vídeos → Entretenimiento', ext: '.mp4' },
    ];

    return (
        <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

            {/* ── Hero ── */}
            <Box sx={{ bgcolor: '#020617', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {/* Navbar */}
                <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <Container maxWidth="lg" sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <BrandLogo size={32} />
                        {/* Language switcher */}
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {(['es', 'en'] as const).map((lng) => (
                                <Box
                                    key={lng}
                                    component="button"
                                    onClick={() => handleLanguageChange(lng)}
                                    sx={{
                                        bgcolor: i18n.language === lng ? 'rgba(37,99,235,0.7)' : 'transparent',
                                        color: i18n.language === lng ? 'white' : 'rgba(255,255,255,0.45)',
                                        border: '1px solid',
                                        borderColor: i18n.language === lng ? 'rgba(37,99,235,0.9)' : 'rgba(255,255,255,0.12)',
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.25,
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        lineHeight: 1.6,
                                        '&:hover': {
                                            color: 'white',
                                            borderColor: 'rgba(255,255,255,0.35)',
                                        },
                                    }}
                                >
                                    {lng}
                                </Box>
                            ))}
                        </Box>
                    </Container>
                </Box>

                <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 6, md: 8 }, alignItems: 'center' }}>

                        {/* Left */}
                        <Box sx={{ flex: '1 1 55%' }}>
                            <Chip
                                label={t('landing.versionChip')}
                                size="small"
                                sx={{ bgcolor: 'rgba(56,189,248,0.12)', color: '#38BDF8', mb: 3, fontWeight: 600, border: '1px solid rgba(56,189,248,0.2)' }}
                            />
                            <Typography
                                variant="h2"
                                component="h1"
                                sx={{ fontWeight: 800, fontSize: { xs: '2.2rem', md: '3rem' }, lineHeight: 1.15, mb: 2.5 }}
                            >
                                {t('landing.heroTitle1')}
                                <br />
                                <Box component="span" sx={{ color: '#38BDF8' }}>{t('landing.heroTitle2')}</Box>
                            </Typography>
                            <Typography sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.55)', mb: 4, lineHeight: 1.75, maxWidth: 460 }}>
                                {t('landing.heroSubtitle')}
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<Download />}
                                    href={DOWNLOAD_URL}
                                    sx={{
                                        bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' },
                                        px: 4, py: 1.5, fontWeight: 600, fontSize: '1rem',
                                        boxShadow: '0 4px 20px rgba(37,99,235,0.4)',
                                    }}
                                >
                                    {t('landing.downloadFree')}
                                </Button>
                                {serviceReachable && (
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        component={RouterLink}
                                        to={routes.dashboard.to()}
                                        startIcon={<Dashboard />}
                                        sx={{
                                            borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)',
                                            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.04)' },
                                            px: 4, py: 1.5, fontWeight: 600, fontSize: '1rem',
                                        }}
                                    >
                                        {t('landing.openDashboard')}
                                    </Button>
                                )}
                            </Stack>
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {[t('landing.badge1'), t('landing.badge2'), t('landing.badge3')].map((label) => (
                                    <Chip
                                        key={label}
                                        label={`✓ ${label}`}
                                        size="small"
                                        sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.1)' }}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        {/* Right — app preview */}
                        <Box sx={{ flex: '1 1 45%', width: '100%', maxWidth: { xs: 420, md: '100%' } }}>
                            <Box sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1.5, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
                                {/* Dashboard header */}
                                <Box sx={{ background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)', px: 2, py: 1.5 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'white', lineHeight: 1 }}>Dashboard</Typography>
                                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', mt: 0.3 }}>{t('landing.dashboardPreview')}</Typography>
                                </Box>
                                {/* KPI cards */}
                                <Box sx={{ p: 1.5, bgcolor: '#F8FAFC' }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 1.5 }}>
                                        {MOCK_KPI.map((kpi) => (
                                            <Box key={kpi.label} sx={{ bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: 1, p: 1, borderTop: `2px solid ${kpi.borderColor}` }}>
                                                <Typography sx={{ fontSize: 9, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>{kpi.label}</Typography>
                                                <Typography sx={{ fontSize: 17, fontWeight: 800, color: kpi.valueColor, lineHeight: 1.3, mt: 0.5 }}>{kpi.value}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                    {/* Rules list */}
                                    <Box sx={{ bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
                                        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #E2E8F0' }}>
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#0F172A' }}>{t('landing.mockLatestRules')}</Typography>
                                        </Box>
                                        {MOCK_DASHBOARD_RULES.map((rule, i) => (
                                            <Box key={rule.name} sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.9, gap: 1, borderBottom: i < MOCK_DASHBOARD_RULES.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981', flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: 10, color: '#1E293B', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rule.name}</Typography>
                                                <Box sx={{ bgcolor: 'rgba(16,185,129,0.1)', px: 0.7, py: 0.2, borderRadius: 0.5, flexShrink: 0 }}>
                                                    <Typography sx={{ fontSize: 9, color: '#10B981', fontWeight: 600 }}>{t('landing.mockActiveStatus')}</Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                    {/* Footer status */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: 10, color: '#94A3B8' }}>{t('landing.mockRulesActive')}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
                                            <Typography sx={{ fontSize: 10, color: '#10B981', fontWeight: 600 }}>{t('landing.mockConnected')}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* ── Features ── */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
                <Box sx={{ textAlign: 'center', mb: 7 }}>
                    <Chip label={t('landing.featuresChip')} sx={{ mb: 2, bgcolor: 'rgba(37,99,235,0.08)', color: '#2563EB', fontWeight: 600, border: '1px solid rgba(37,99,235,0.15)' }} />
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 700, color: '#0F172A', mb: 1.5 }}>
                        {t('landing.featuresTitle')}
                    </Typography>
                    <Typography sx={{ color: '#64748B', fontSize: '1.05rem', maxWidth: 480, mx: 'auto' }}>
                        {t('landing.featuresSubtitle')}
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    gap: 3,
                }}>
                    {FEATURES.map((f) => (
                        <Card
                            key={f.title}
                            sx={{
                                border: '1px solid #E2E8F0', boxShadow: 'none',
                                transition: 'box-shadow 0.2s, transform 0.2s',
                                '&:hover': { boxShadow: '0 8px 28px rgba(0,0,0,0.09)', transform: 'translateY(-4px)' },
                            }}
                        >
                            <CardContent sx={{ p: 3.5 }}>
                                <Box sx={{ bgcolor: f.bg, color: f.color, width: 50, height: 50, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                                    {f.icon}
                                </Box>
                                <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A', mb: 1, fontSize: '1rem' }}>
                                    {f.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.75 }}>
                                    {f.desc}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Container>

            {/* ── How it works ── */}
            <Box sx={{ bgcolor: '#0F172A', py: { xs: 8, md: 11 } }}>
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center', mb: 7 }}>
                        <Chip label={t('landing.installChip')} sx={{ mb: 2, bgcolor: 'rgba(56,189,248,0.12)', color: '#38BDF8', fontWeight: 600, border: '1px solid rgba(56,189,248,0.2)' }} />
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 700, color: 'white' }}>
                            {t('landing.installTitle')}
                        </Typography>
                    </Box>
                    <Stack spacing={2}>
                        {STEPS.map((step) => (
                            <Box
                                key={step.n}
                                sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: 3,
                                    p: 3, bgcolor: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2,
                                }}
                            >
                                <Box sx={{
                                    bgcolor: '#2563EB', color: 'white', width: 44, height: 44,
                                    borderRadius: '50%', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0,
                                }}>
                                    {step.n}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>{step.title}</Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', lineHeight: 1.75 }}>
                                        {step.desc}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                    <Box sx={{ textAlign: 'center', mt: 5 }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Download />}
                            href={DOWNLOAD_URL}
                            sx={{
                                bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' },
                                px: 5, py: 1.5, fontWeight: 600, fontSize: '1rem',
                                boxShadow: '0 4px 20px rgba(37,99,235,0.4)',
                            }}
                        >
                            {t('landing.downloadButton')}
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* ── Planes ── */}
            <Container maxWidth="md" sx={{ py: { xs: 8, md: 11 } }}>
                <Box sx={{ textAlign: 'center', mb: 7 }}>
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 700, color: '#0F172A', mb: 1.5 }}>
                        {t('landing.plansTitle')}
                    </Typography>
                    <Typography sx={{ color: '#64748B', fontSize: '1.05rem' }}>
                        {t('landing.plansSubtitle')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                    {/* Free */}
                    <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <Typography variant="overline" sx={{ color: '#64748B', fontWeight: 700 }}>{t('landing.freeTier')}</Typography>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#0F172A', my: 1 }}>{t('landing.freePrice')}</Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.88rem', mb: 3 }}>{t('landing.freePriceDesc')}</Typography>
                            <Stack spacing={1.5} sx={{ mb: 4, flexGrow: 1 }}>
                                {FREE_FEATURES.map((item) => (
                                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <CheckCircle sx={{ fontSize: 17, color: '#10B981', flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '0.9rem', color: '#475569' }}>{item}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                            <Button variant="outlined" fullWidth href={DOWNLOAD_URL} sx={{ py: 1.2, fontWeight: 600, borderColor: '#2563EB', color: '#2563EB', '&:hover': { bgcolor: 'rgba(37,99,235,0.05)' } }}>
                                {t('landing.downloadFreeBtn')}
                            </Button>
                        </CardContent>
                    </Card>
                    {/* Premium */}
                    <Card sx={{ border: '2px solid #2563EB', boxShadow: '0 8px 32px rgba(37,99,235,0.12)', position: 'relative', overflow: 'visible', display: 'flex', flexDirection: 'column' }}>
                        <Chip
                            label={t('landing.premiumComingSoon')}
                            size="small"
                            sx={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', bgcolor: '#2563EB', color: 'white', fontWeight: 700, px: 1 }}
                        />
                        <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <Typography variant="overline" sx={{ color: '#2563EB', fontWeight: 700 }}>{t('landing.premiumTier')}</Typography>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#0F172A', my: 1 }}>—</Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.88rem', mb: 3 }}>{t('landing.premiumDesc')}</Typography>
                            <Stack spacing={1.5} sx={{ mb: 4, flexGrow: 1 }}>
                                {PREMIUM_FEATURES.map((item) => (
                                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <CheckCircle sx={{ fontSize: 17, color: '#2563EB', flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '0.9rem', color: '#475569' }}>{item}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                            <Button variant="contained" fullWidth disabled sx={{ py: 1.2, fontWeight: 600 }}>
                                {t('landing.availableSoon')}
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Container>

            {/* ── Footer ── */}
            <Box sx={{ bgcolor: '#020617', color: 'white', py: 5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <Container maxWidth="lg">
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { sm: 'center' },
                        gap: 2,
                    }}>
                        <BrandLogo size={36} />
                        <Stack spacing={0.5} sx={{ textAlign: { sm: 'right' } }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem' }}>
                                {t('landing.footerCopy', { year: new Date().getFullYear() })}
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.2)' }}>
                                {t('landing.footerMadeBy')}{' '}
                                <Box
                                    component="a"
                                    href="https://www.linkedin.com/in/carlos-serrano-querol/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: '#38BDF8', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    Carlos Serrano
                                </Box>
                            </Typography>
                        </Stack>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default LandingPage;
